
// Packages
import { defineComponent } from 'vue';
import { mapState } from 'pinia';

// Helpers
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getDomainUrl, DOMAINS_NAMES } from '@white-label-helper/switch-app';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import { usePartnerConfigHelper } from '@white-label-helper/shared-helpers';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';

// Stores
import { useDiscountCodeStore } from '@white-label-store/discount-code';
import { useUserInfoStore } from '@white-label-store/user-info';

// Constants
import {
  APP_HERO_PRODUCTS,
  NAMED_ROUTES,
  PROGRESS_INDICATOR_PAGES,
  PRODUCT_TYPES,
  FEATURE_FLAGS,
  BOOKING_PORTAL_FLOW,
} from '@white-label-configuration/constants';

import { isBookingPortalFlow } from '@white-label-helper/booking-portal-flow';

// Components
import { useCartCheckoutStore } from '@white-label-store/cart-checkout';

// Components
import ProgressIndicator from '../progress-indicator/progress-indicator.vue';
import Navbar from '../navbar/navbar.vue';

export default defineComponent({
  name: 'CheckoutNavBar',

  components: {
    NavbarCheckout: Navbar,
    ProgressIndicator,
  },

  data() {
    const {
      isCrossSellFlowEnabled,
      isExtrasProductFlowEnabled,
    } = usePartnerConfigHelper();

    return {
      NAMED_ROUTES,
      isCrossSellFlowEnabled,
      isExtrasProductFlowEnabled,
    };
  },

  computed: {
    ...mapState(useCartCheckoutStore, {
      readCartItems: 'readCartItems',
      readCartItemsType: 'readCartItemsType',
    }),
    ...mapState(useUserInfoStore, {
      outboundFlightSelected: 'readOutboundFlightLookupSelected',
    }),
    ...mapState(useDiscountCodeStore, {
      discountQuery: 'discountQuery',
    }),
    displayFlightLookup() {
      const isParkingProduct = getAppHeroProduct(PRODUCT_TYPES.PARKING) === PRODUCT_TYPES.PARKING;
      const displayFlightLookupForParking = isParkingProduct && getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP_PARKING) === true;
      return getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP) === true
        && getAppVariable('is_flight_lookup_enabled')
        && displayFlightLookupForParking;
    },
    queryParams() {
      let query = '';

      if (this.discountQuery) {
        query = `${this.discountQuery}`;
      }
      if (this.displayFlightLookup && this.outboundFlightSelected?.id) {
        const separator = query ? '&' : '';
        query += `${separator}outboundFlight=${this.outboundFlightSelected.id}`;
      }

      return query;
    },

    cartItemsType() {
      return this.readCartItemsType;
    },

    isLounges(): boolean {
      return this.cartItemsType === 'lounges';
    },

    cartItems() {
      return this.readCartItems;
    },

    isCancellationProtectionAvailable(): boolean {
      return this.cartItems?.[0]?.inventory_option?.details
        ?.cancellation_protection?.is_available;
    },

    travelExtrasFlowEnabled(): boolean {
      return this.isCrossSellFlowEnabled;
    },

    bookingAppUrls(): { [key: string]: string } {
      const flowPrefixPath = this.isBookingPortalFlowEnabled ? `/${BOOKING_PORTAL_FLOW.AUTH0_FLOW}` : '';
      const urls = {
        home: `${getDomainUrl(DOMAINS_NAMES.ECOMMERCE)}${flowPrefixPath}${NAMED_ROUTES.home}`,
        search: `${getDomainUrl(DOMAINS_NAMES.ECOMMERCE)}${flowPrefixPath}${NAMED_ROUTES.search}`,
        select_extras: '',
        checkout: '',
      }

      urls.checkout = this.isBookingPortalFlowEnabled ? PROGRESS_INDICATOR_PAGES.booking_portal_checkout : PROGRESS_INDICATOR_PAGES.checkout;

      if (this.travelExtrasFlowEnabled) {
        urls.select_extras = `${getDomainUrl(DOMAINS_NAMES.ECOMMERCE)}${flowPrefixPath}${NAMED_ROUTES.travel_extras}`
      } else if (this.isExtrasProductFlowEnabled) {
        urls.select_extras = `${getDomainUrl(DOMAINS_NAMES.ECOMMERCE)}${flowPrefixPath}${NAMED_ROUTES.select_extras.product}`;
      }

      return urls;
    },

    shouldSkipExtrasStep(): boolean {
      return this.travelExtrasFlowEnabled
        ? (localStorage.getItem('skippedExtrasStep') ?? '') === this.$route.query['token']
        : this.bookingAppUrls['select_extras'] === '';
    },
    isBookingPortalFlowEnabled(): boolean {
      return isBookingPortalFlow();
    },

    /**
     * Returns boolean based on if the $route.name exists in Indicators list of routes.
     * If in dedicated wl flow then removes 'dedicated-wl-' out from the route name,
     * so we can use normal route naming convention.
     */
    displayProgressIndicator(): boolean {
      const routeName =
        (getFeatureFlag(FEATURE_FLAGS.CUS_2533_DEDICATED_BOOKING_FLOW) && this.$route.name != null)
          ? this.$route.name.replace('dedicated-wl-', '')
          : this.$route.name;

      return (
        this.cartItemsType !== '' &&
        Object.values(PROGRESS_INDICATOR_PAGES).includes(routeName)
      );
    },

    partner_name() {
      return getAppVariable('partner_name');
    },

    logoURL() {
      return getAppVariable('logo');
    },

    logoRedirectURL() {
      return (
        getAppVariable('logo_redirect_url')
        || getDomainUrl(DOMAINS_NAMES.ECOMMERCE)
      );
    },
  }
});
